import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Container, CodeTag } from "@/components/Section";
import { Reveal, LineReveal } from "@/components/motion";
import Seo from "@/components/Seo";
import { fetchProjects } from "@/lib/api";

const FILTERS = ["All", "Infrastructure", "Site Development", "Heavy", "Concrete"];
const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-7", "lg:col-span-5"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetchProjects().then(setProjects).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "All") return projects;
    return projects.filter((p) => p.category === filter);
  }, [projects, filter]);

  return (
    <div data-testid="projects-page">
      <Seo
        title="Projects | Heavy Civil Work Across the Lower Mainland — APRASIM"
        description="APRASIM projects across BC: Columbia Valley culvert replacement (MoTI), Richmond parking facility (Defence Construction Canada), City of Coquitlam roadworks, lockblock walls, sediment ponds, site clearing and remote road works."
        path="/projects"
      />
      {/* HERO */}
      <section className="border-b-2 border-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 pointer-events-none" />
        <Container className="relative py-16 sm:py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <Reveal y={20}>
              <CodeTag>PORTFOLIO / 2020 — 2026</CodeTag>
            </Reveal>
            <LineReveal
              className="mt-6 font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92]"
              delay={0.15}
              lines={[<>Field-proven</>, <><span className="text-[#FF4F00]">deliveries</span></>]}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 text-lg text-neutral-700 leading-relaxed max-w-2xl">
                Work delivered across British Columbia — from municipal corridors and federal sites to lockblock walls, sediment ponds and remote forest service roads, with APRASIM crews executing the civil scope on every site.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="col-span-12 lg:col-span-4 flex flex-wrap content-end gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] brut-border transition-colors duration-300 ${
                  filter === f ? "bg-black text-white" : "bg-[#F0F0EC] text-black hover:bg-[#FF4F00] hover:text-black"
                }`}
              >
                {f}
              </button>
            ))}
          </Reveal>
        </Container>
      </section>

      {/* TETRIS GRID */}
      <section className="border-b-2 border-black bg-[#E5E5DF]">
        <Container className="py-16 sm:py-20">
          {filtered.length === 0 ? (
            <div className="py-32 text-center font-mono text-sm uppercase tracking-[0.25em]">
              No projects in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={Math.min((i % 2) * 0.1, 0.2)} className={SPANS[i % SPANS.length]}>
                  <Link
                    to={`/projects/${p.slug}`}
                    data-testid={`project-card-${p.slug}`}
                    className="group block relative brut-border bg-black overflow-hidden"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-[320px] sm:h-[400px] object-cover img-gray img-zoom"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                    </div>
                    <div className="absolute top-0 left-0 m-4 bg-[#F0F0EC] font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1 border-2 border-black">
                      {p.code}
                    </div>
                    <div className="absolute top-0 right-0 m-4 bg-[#FF4F00] text-black font-mono text-[10px] tracking-[0.3em] uppercase px-2 py-1">
                      {p.category}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6 text-[#F0F0EC]">
                      <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-300">
                        <span>{p.location}</span>
                        <span>{p.year}</span>
                      </div>
                      <h3 className="mt-2 font-display text-2xl sm:text-3xl uppercase tracking-tight transition-transform duration-500 group-hover:-translate-y-1">
                        {p.title}
                      </h3>
                      <div className="flex items-center justify-between mt-3 max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100 transition-[max-height,opacity] duration-500 overflow-hidden">
                        <span className="font-mono text-sm">{p.value}</span>
                        <span className="font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 text-[#FF4F00]">
                          Case study <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}
